import { useMemo, useState } from "react";
import styles from "./FilterCard.module.scss";

const PRESET_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  custom: "Custom",
};

// Every filter's local edit state, seeded from what the agent inferred.
const buildInitialSelections = (filters) => {
  const state = {};
  filters.forEach((f) => {
    if (f.type === "multi" || f.type === "single") {
      state[f.id] = { selected: f.selected || [] };
    } else if (f.type === "range") {
      state[f.id] = {
        start: f.start || "",
        end: f.end || "",
        startTime: f.start_time || "",
        endTime: f.end_time || "",
        preset: f.preset || null,
      };
    } else if (f.type === "date") {
      state[f.id] = { date: f.date || "", preset: f.preset || null };
    }
  });
  return state;
};

const allOptionIds = (filter) => (filter.options || []).map((o) => o.id);

const isFilterSatisfied = (filter, value) => {
  if (!filter.required) return true;
  if (filter.type === "multi" || filter.type === "single") {
    return Boolean(value?.selected?.length);
  }
  if (filter.type === "range") {
    return Boolean(value?.start) && Boolean(value?.end);
  }
  if (filter.type === "date") {
    return Boolean(value?.date);
  }
  return true;
};

const buildConfirmationMessage = (prompt, selections) => {
  const lines = [`Generate ${prompt.name} with these filters:`];

  prompt.filters.forEach((filter) => {
    const value = selections[filter.id];

    if (filter.type === "multi" || filter.type === "single") {
      const chosen = value?.selected || [];
      if (!chosen.length) return; // nothing picked on an optional filter = leave at default

      const everyOption = allOptionIds(filter);
      const isEverything =
        everyOption.length > 0 && chosen.length === everyOption.length;

      if (isEverything) {
        lines.push(`- ${filter.label}: All ${filter.label.toLowerCase()}`);
      } else {
        const labelled = chosen
          .map((id) => {
            const option = (filter.options || []).find((o) => o.id === id);
            return option ? `${option.label} (ID: ${option.id})` : id;
          })
          .join(", ");
        lines.push(`- ${filter.label}: ${labelled}`);
      }
      return;
    }

    if (filter.type === "range") {
      if (!value?.start && !value?.end) return;
      let text = `${value.start || "?"} to ${value.end || "?"}`;
      if (filter.time && (value.startTime || value.endTime)) {
        text += ` (${value.startTime || "00:00"} to ${value.endTime || "23:59"})`;
      }
      lines.push(`- ${filter.label}: ${text}`);
      return;
    }

    if (filter.type === "date") {
      if (!value?.date) return;
      lines.push(`- ${filter.label}: ${value.date}`);
    }
  });

  if (prompt.comment) {
    lines.push(`- Comment: ${prompt.comment}`);
  }

  return lines.join("\n");
};

// One row of tappable pills — radio semantics for "single", checkbox for
// "multi". A synthetic "All ..." option is prepended whenever the filter has
// a live option list; picking it clears every individual pick.
const ChoiceRow = ({ filter, value, onChange, disabled }) => {
  const selected = value?.selected || [];
  const options = filter.options || [];
  const everyId = allOptionIds(filter);
  const allLabel = `All ${filter.label.toLowerCase()}`;
  const isAllSelected =
    everyId.length > 0 && selected.length === everyId.length;

  const toggle = (id) => {
    if (filter.type === "single") {
      onChange({ selected: selected[0] === id ? [] : [id] });
      return;
    }
    onChange({
      selected: selected.includes(id)
        ? selected.filter((v) => v !== id)
        : [...selected, id],
    });
  };

  const selectAll = () => onChange({ selected: isAllSelected ? [] : everyId });

  return (
    <div className={styles.filterCard__optionsRow}>
      {options.length > 0 && (
        <button
          type="button"
          className={`${styles.filterCard__option} ${
            isAllSelected ? styles["filterCard__option--selected"] : ""
          }`}
          disabled={disabled}
          onClick={selectAll}
        >
          {allLabel}
        </button>
      )}
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={`${styles.filterCard__option} ${
            !isAllSelected && selected.includes(option.id)
              ? styles["filterCard__option--selected"]
              : ""
          }`}
          disabled={disabled}
          onClick={() => toggle(option.id)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

const RangeRow = ({ filter, value, onChange, disabled }) => {
  const preset = value?.preset;
  const showCustom = preset === "custom" || !preset;

  const pickPreset = (p) => {
    if (p === "custom") {
      onChange({ ...value, preset: "custom" });
      return;
    }
    // Presets other than "custom" are resolved by the agent from plain text —
    // picking one here just flags intent; re-asking resolves exact dates.
    onChange({ ...value, preset: p, start: "", end: "" });
  };

  return (
    <div className={styles.filterCard__optionsRow}>
      {(filter.presets || []).map((p) => (
        <button
          key={p}
          type="button"
          className={`${styles.filterCard__option} ${
            preset === p ? styles["filterCard__option--selected"] : ""
          }`}
          disabled={disabled}
          onClick={() => pickPreset(p)}
        >
          {PRESET_LABELS[p] || p}
        </button>
      ))}
      {showCustom && (
        <div className={styles.filterCard__customDates}>
          <input
            type="date"
            className={styles.filterCard__dateInput}
            value={value?.start || ""}
            disabled={disabled}
            onChange={(e) =>
              onChange({ ...value, start: e.target.value, preset: "custom" })
            }
          />
          <span>to</span>
          <input
            type="date"
            className={styles.filterCard__dateInput}
            value={value?.end || ""}
            disabled={disabled}
            onChange={(e) =>
              onChange({ ...value, end: e.target.value, preset: "custom" })
            }
          />
          {filter.time && (
            <>
              <input
                type="time"
                className={styles.filterCard__dateInput}
                value={value?.startTime || ""}
                disabled={disabled}
                onChange={(e) =>
                  onChange({ ...value, startTime: e.target.value })
                }
              />
              <span>to</span>
              <input
                type="time"
                className={styles.filterCard__dateInput}
                value={value?.endTime || ""}
                disabled={disabled}
                onChange={(e) =>
                  onChange({ ...value, endTime: e.target.value })
                }
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const DateRow = ({ filter, value, onChange, disabled }) => {
  const preset = value?.preset;
  const showCustom = preset === "custom" || !preset;

  const pickPreset = (p) => {
    if (p === "custom") {
      onChange({ ...value, preset: "custom" });
      return;
    }
    onChange({ ...value, preset: p, date: "" });
  };

  return (
    <div className={styles.filterCard__optionsRow}>
      {(filter.presets || []).map((p) => (
        <button
          key={p}
          type="button"
          className={`${styles.filterCard__option} ${
            preset === p ? styles["filterCard__option--selected"] : ""
          }`}
          disabled={disabled}
          onClick={() => pickPreset(p)}
        >
          {PRESET_LABELS[p] || p}
        </button>
      ))}
      {showCustom && (
        <input
          type="date"
          className={styles.filterCard__dateInput}
          value={value?.date || ""}
          disabled={disabled}
          onChange={(e) =>
            onChange({ ...value, date: e.target.value, preset: "custom" })
          }
        />
      )}
    </div>
  );
};

// One combined card for every filter a report needs (stores, date range,
// order source, ...), pre-filled from what the agent inferred, with a
// Comment row for anything that falls outside the formal filters. Answers
// with one deterministic "Generate ... with these filters:" chat message —
// same mechanism as every other interactive prompt.
const FilterCard = ({ prompt, onAnswer, disabled = false }) => {
  const [selections, setSelections] = useState(() =>
    buildInitialSelections(prompt.filters),
  );
  const [touched, setTouched] = useState(() => new Set());
  const [comment, setComment] = useState(prompt.comment || "");
  const [submitted, setSubmitted] = useState(false);
  const [submittedSelections, setSubmittedSelections] = useState(null);

  const updateFilter = (filterId, value) => {
    setSelections((prev) => ({ ...prev, [filterId]: value }));
    setTouched((prev) => new Set(prev).add(filterId));
  };

  const isReady = useMemo(
    () => prompt.filters.every((f) => isFilterSatisfied(f, selections[f.id])),
    [prompt.filters, selections],
  );

  const handleReset = () => {
    setSelections(buildInitialSelections(prompt.filters));
    setTouched(new Set());
    setComment(prompt.comment || "");
  };

  const handleGenerate = () => {
    const message = buildConfirmationMessage(
      { ...prompt, comment },
      selections,
    );
    setSubmittedSelections({ selections: { ...selections }, comment });
    setSubmitted(true);
    onAnswer(message);
  };

  const handleEditFilters = () => setSubmitted(false);

  if (submitted && submittedSelections) {
    return (
      <div className={styles.filterCard}>
        <div className={styles.filterCard__summary}>
          {prompt.filters.map((filter) => {
            const value = submittedSelections.selections[filter.id];
            let text = "—";
            if (filter.type === "multi" || filter.type === "single") {
              const chosen = value?.selected || [];
              const everyId = allOptionIds(filter);
              if (!chosen.length) text = `All ${filter.label.toLowerCase()}`;
              else if (everyId.length && chosen.length === everyId.length)
                text = `All ${filter.label.toLowerCase()}`;
              else
                text = chosen
                  .map(
                    (id) =>
                      (filter.options || []).find((o) => o.id === id)?.label ||
                      id,
                  )
                  .join(", ");
            } else if (filter.type === "range") {
              text =
                value?.start || value?.end
                  ? `${value.start || "?"} to ${value.end || "?"}`
                  : "Default";
            } else if (filter.type === "date") {
              text = value?.date || "Default";
            }
            return (
              <div key={filter.id} className={styles.filterCard__summaryRow}>
                <span className={styles.filterCard__summaryLabel}>
                  {filter.label}
                </span>
                <span>{text}</span>
              </div>
            );
          })}
          {submittedSelections.comment && (
            <div className={styles.filterCard__summaryRow}>
              <span className={styles.filterCard__summaryLabel}>Comment</span>
              <span>{submittedSelections.comment}</span>
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.filterCard__editLink}
          onClick={handleEditFilters}
          disabled={disabled}
        >
          Edit filters
        </button>
      </div>
    );
  }

  return (
    <div className={styles.filterCard}>
      <div className={styles.filterCard__header}>
        <span className={styles.filterCard__icon} aria-hidden="true">
          🧾
        </span>
        <div>
          <p className={styles.filterCard__name}>{prompt.name}</p>
          {prompt.description && (
            <p className={styles.filterCard__description}>
              {prompt.description}
            </p>
          )}
        </div>
      </div>

      {prompt.filters.map((filter) => (
        <div key={filter.id} className={styles.filterCard__row}>
          <div className={styles.filterCard__rowHead}>
            <span className={styles.filterCard__label}>{filter.label}</span>
            {!filter.required && (
              <span className={styles.filterCard__optional}>Optional</span>
            )}
            {filter.inferred && !touched.has(filter.id) && (
              <span className={styles.filterCard__inferredTag}>
                From your question
              </span>
            )}
          </div>

          {(filter.type === "multi" || filter.type === "single") && (
            <ChoiceRow
              filter={filter}
              value={selections[filter.id]}
              disabled={disabled}
              onChange={(v) => updateFilter(filter.id, v)}
            />
          )}
          {filter.type === "range" && (
            <RangeRow
              filter={filter}
              value={selections[filter.id]}
              disabled={disabled}
              onChange={(v) => updateFilter(filter.id, v)}
            />
          )}
          {filter.type === "date" && (
            <DateRow
              filter={filter}
              value={selections[filter.id]}
              disabled={disabled}
              onChange={(v) => updateFilter(filter.id, v)}
            />
          )}
        </div>
      ))}

      <div className={styles.filterCard__row}>
        <div className={styles.filterCard__rowHead}>
          <span className={styles.filterCard__label}>Comment</span>
          <span className={styles.filterCard__optional}>Optional</span>
        </div>
        <textarea
          className={styles.filterCard__comment}
          value={comment}
          disabled={disabled}
          placeholder="Anything else to apply, e.g. 'only the morning shift, before 2pm'"
          onChange={(e) => setComment(e.target.value)}
        />
        {comment && (
          <p className={styles.filterCard__commentStatus}>
            No matching filter. Temi will apply this when analysing the results.
          </p>
        )}
      </div>

      <div className={styles.filterCard__footer}>
        <span className={styles.filterCard__status}>
          {isReady ? "Ready" : "Choose the required filters to continue"}
        </span>
        <div className={styles.filterCard__footerActions}>
          <button
            type="button"
            className={styles.filterCard__reset}
            disabled={disabled}
            onClick={handleReset}
          >
            Reset
          </button>
          <button
            type="button"
            className={styles.filterCard__generate}
            disabled={disabled || !isReady}
            onClick={handleGenerate}
          >
            Generate
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterCard;
